/**
 * 계약 금액 계산. 견적 `calc.ts` 와 같은 모양이다 — 순수 함수뿐이고 저장소·시간·
 * 난수를 모른다. **RPC 는 계산하지 않는다**(메모리 「확정 사항」).
 *
 * ## 금액의 정의 (2026-09-22 오너 확정 · 노트 `12` 부가세 표)
 *
 * `total_amount` 는 **고객이 실제로 낼 돈**이고 **항상** `supply_amount + vat_amount` 다.
 * 지급회차도 계약서 금액 칸도 이 수를 쓴다 — 「부가세 별도」 계약도 계약금 30% 는
 * 330만원이고, 인쇄되는 수도 1,100만원이다(`printedAmount` 주석에 왜인지 있다).
 *
 * |  | 부가세 별도 | 부가세 포함 |
 * |---|---|---|
 * | supply / vat / total | 1,000만 / 100만 / 1,100만 | 같다 |
 * | **인쇄 금액** | **1,100만원** | **1,100만원** |
 * | `vat_included` | false | true — **표기만 가른다** |
 *
 * ## 부가세는 빼서 낸다
 *
 * **`vat = total − supply`.** 공급가와 세액을 따로 반올림하면 1원이 어긋나
 * `0019` 의 `contract_amount_split` 에 걸린다. 한쪽만 반올림하고 나머지는 뺀다.
 */

import { subjectJosa } from "../../lib/josa";
import { formatNumber } from "../../lib/format";
import { checked, checkedMoney } from "../money/checked";
import { amountsFromSupply, amountsFromTotal, type VatSplit } from "../money/vat";
import {
  DAYS_PER_YEAR,
  FTC_MIN_WARRANTY_MONTHS,
  PENALTY_RATE_CAP_BP,
  warrantyLabelKey,
} from "./defaults";
import {
  ClauseSource,
  PaymentPlanRow,
  SpecialTerm,
  WarrantyTerm,
} from "./types";

/** 만분율(basis point)의 밑. 10000 = 100%. `0019` 의 `*_bp` 열과 같은 자다. */
export const BP_SCALE = 10_000;

/** 지급회차 비율의 합. 모자라거나 넘치면 받을 돈이 도급금액과 달라진다. */
export const RATIO_TOTAL_BP = BP_SCALE;

// 계약 세 수 — 부가세 나누기는 `money/vat.ts` 한 곳이다.
export type ContractAmounts = VatSplit;

/**
 * 화면에 입력한 한 칸이 무엇인가. **`vat_included` 와 무관하다** —
 * 「부가세 별도」 계약서에도 총액을 적는 회사와 공급가를 적는 회사가 둘 다 있다
 * (그래서 `0019` 가 한쪽으로 막지 않는다).
 */
export type AmountBasis = "TOTAL" | "SUPPLY";

export const contractAmounts = (
  amount: number,
  basis: AmountBasis
): ContractAmounts =>
  basis === "TOTAL" ? amountsFromTotal(amount) : amountsFromSupply(amount);

/**
 * 저장 직전의 마지막 검사.
 *
 * `supply + vat = total` 은 DB 가 본다. **DB 가 못 잡는 것은 여기서 잡는다** —
 * `vat_included` 가 참인데 세액이 0 인 행은 부가세를 빠뜨린 계산이 조용히 굳은
 * 것이다(`0019` 주석).
 *
 * **「0 이면 막는다」 가 아니라 「나왔어야 할 세액이 0 이면 막는다」 이다.** 총액이
 * 0 인 초안은 물론이고 총액 5원짜리도 반올림하면 세액이 정말 0 이다(실측:
 * `amountsFromTotal(5)` → 공급가 5·세액 0). 상수로 자르면 **계산이 낸 값을 계산이
 * 거절하는** 구간이 생기므로, 같은 총액을 다시 풀어 견준다.
 */
export const assertContractAmounts = (
  amounts: ContractAmounts,
  vatIncluded: boolean
): void => {
  checkedMoney(amounts.total_amount, "계약금액");
  checkedMoney(amounts.supply_amount, "공급가액");
  checkedMoney(amounts.vat_amount, "부가세액");

  if (amounts.supply_amount + amounts.vat_amount !== amounts.total_amount) {
    throw new Error("공급가액 + 부가세액이 계약금액과 맞지 않습니다");
  }
  if (
    vatIncluded &&
    amounts.vat_amount === 0 &&
    amountsFromTotal(amounts.total_amount).vat_amount > 0
  ) {
    throw new Error("「부가세 포함」 계약의 부가세액이 0원입니다 — 금액을 확인해 주세요");
  }
};

export interface PrintedAmount {
  /** 계약서 금액 칸에 찍을 수. **언제나 총액이다.** */
  amount: number;
  /** 본문 표기가 「포함」인가 「별도」인가. 인쇄층이 문구를 만든다. */
  vat_included: boolean;
  supply_amount: number;
  vat_amount: number;
}

/**
 * 계약서 금액 칸. **언제나 총액을 찍는다** (2026-09-22 오너 확정).
 *
 * ## 왜 총액인가
 *
 * 공정위 제3조 ③ 본문이 「총 공사금액 : ( ) **(부가가치세 포함)**」 이다 — 축자
 * 원문이라 고칠 수 없다. 여기에 「부가세 별도」 계약이라고 공급가 1,000만을 적으면
 * 문서는 「1,000만원(부가세 포함)」 이 되고, **대법원은 약정액에 부가세가 포함된
 * 것으로 본다**(99다62821). 시공사가 100만원을 잃는 경로가 정확히 이것이다.
 * 국토부 표지 5번도 같은 문구라 두 공식 서식이 같다.
 *
 * ## 왜 서식을 보지 않는가
 *
 * 한때 `CUSTOM` 에서만 `vat_included` 가 인쇄를 정하게 두었는데, 세 가지가 따라
 * 나왔다(2026-09-22 리뷰). ① **조항 한 글자를 고치면 금액 칸이 뒤집혔다** —
 * 관할법원 조를 손보는 순간 `CUSTOM` 이 되고 총액이 공급가로 바뀐다. 발송한
 * 계약이면 고객이 본 수와 갈린다 ② **지급회차 합과 어긋났다**(본문 1,000만 ·
 * 표 1,100만) ③ 총액 1,000만을 친 계약에 909만이 찍혔다.
 *
 * ## 왜 문자열을 안 만드는가
 *
 * `toLocaleString` 은 런타임 ICU 에 따라 결과가 갈린다. 이 값이
 * `contract_snapshot.canonical` 에 실리면 **해시가 실행 환경에 의존**하고,
 * 「그때 그 문서였다」 를 증명하려던 목적이 통째로 깨진다(`0019` 스냅샷 주석).
 * 수와 사실만 내보내고 문구는 인쇄층이 만든다.
 *
 * ⚠️ **인쇄·스냅샷은 `contract.supply_amount` 를 직접 읽지 않는다.** 이 함수를
 * 거치지 않으면 「언제나 총액」 이 깨지고 위 세 결함이 그대로 돌아온다 — 타입이
 * 막아 주지 못하는 자리라 규약으로 남긴다(묶음 5·6).
 */
export const printedAmount = (
  amounts: ContractAmounts,
  vatIncluded: boolean
): PrintedAmount => ({
  amount: amounts.total_amount,
  vat_included: vatIncluded,
  supply_amount: amounts.supply_amount,
  vat_amount: amounts.vat_amount,
});

/* ── 지급회차 ─────────────────────────────────────────────────── */

/**
 * 비율이 성립하는가. **합이 10000 이어야 한다** — 9999 면 1/10000 이 영원히
 * 청구되지 않고, 10001 이면 도급금액보다 더 받는 계약서가 나간다.
 *
 * 0 원짜리 회차는 회차가 아니다(`0019` 의 `ratio_bp > 0`).
 */
export const assertPaymentRatios = (ratios: readonly number[]): void => {
  if (ratios.length === 0) {
    throw new Error("지급회차가 없습니다");
  }
  for (const bp of ratios) {
    if (!Number.isInteger(bp) || bp <= 0 || bp > BP_SCALE) {
      throw new Error("지급 비율은 1 이상 10000 이하의 정수여야 합니다");
    }
  }
  const sum = ratios.reduce((a, b) => a + b, 0);
  if (sum !== RATIO_TOTAL_BP) {
    throw new Error(
      `지급 비율의 합이 100% 가 아닙니다 (${formatNumber(sum / 100)}%)`
    );
  }
};

/**
 * 총액을 비율대로 나눈다. **내림 + 마지막 회차가 나머지를 흡수**한다.
 *
 * 33.33% 씩 셋으로 나누면 내림만으로 1~2원이 남는다. 남긴 채로 두면 회차 합이
 * 도급금액보다 적어 **잔금을 다 받아도 미수금이 남는 계약서**가 된다. 마지막
 * 회차에 몰아넣는다 — 실무에서도 잔금이 끝수를 먹는다.
 */
export const splitPaymentAmounts = (
  total: number,
  ratios: readonly number[]
): number[] => {
  checkedMoney(total, "계약금액");
  assertPaymentRatios(ratios);

  const amounts = ratios.map((bp) =>
    Math.floor(checked(total * bp, "회차 금액") / BP_SCALE)
  );
  const assigned = amounts.reduce((a, b) => a + b, 0);
  amounts[amounts.length - 1] += total - assigned;
  return amounts;
};

export interface PaymentPlanResult extends PaymentPlanRow {
  amount: number;
}

/**
 * 지급회차에 금액을 채운다. `seq` 는 **받은 순서 그대로** 1부터 다시 매긴다 —
 * 마지막 회차가 끝수를 먹으므로 「마지막이 어디인가」 가 배열 순서와 어긋나면
 * 끝수가 중간 회차에 붙는다.
 */
export const planPayments = (
  total: number,
  rows: readonly PaymentPlanRow[]
): PaymentPlanResult[] => {
  const amounts = splitPaymentAmounts(
    total,
    rows.map((r) => r.ratio_bp)
  );
  return rows.map((row, i) => {
    if (row.trigger_text.trim() === "") {
      throw new Error(`${i + 1}회차의 지급 시기가 비어 있습니다`);
    }
    return { ...row, seq: i + 1, amount: amounts[i] };
  });
};

/* ── 지연에 따른 배상 ─────────────────────────────────────────── */

/**
 * 이 서식은 **지체상금**(일당 천분율 × 도급금액)을 쓰는가.
 *
 * 공정위 제10079호는 안 쓴다 — 제6조가 **연체이율**(연 %)을 쓰는 다른 셈이다.
 * 일당 10bp 를 그 빈칸에 적으면 「0.1%의 연체이율」 이 되고, 일당으로 읽으면
 * 연 36.5% 라 「시중은행 범위 내」 라는 단서를 대놓고 어긴다.
 *
 * **지연이자 칸은 세 서식이 다 쓴다** — 국토부도 제28조 ③ 과 표지 13번에 있다.
 * 그래서 그쪽에는 판정 함수를 두지 않는다(`lateInterestOf` 를 그냥 쓴다).
 *
 * **칸이 없는 서식에는 그 수를 찍지 않는다**(2026-09-22 오너 확정). 화면도 이 함수를
 * 보고 입력칸을 감춘다 — 적을 데 없는 값을 받아 두면 「적었는데 안 나온다」 가 된다.
 */
export const usesDelayPenalty = (source: ClauseSource): boolean =>
  source !== "FTC_10079";

export interface LateInterestInput {
  /**
   * 이자가 붙는 **원금**. 무엇인지는 **호출부가 정한다** — 조항마다 다르다.
   *
   * | 조항 | 기준 |
   * |---|---|
   * | 공정위 제6조 ① (고객이 대금을 늦게 냄) | **지연한 공사금액** |
   * | 공정위 제6조 ② (시공사가 완공을 늦춤) | **고객이 이미 낸 돈** |
   * | 국토부 제28조 ③ (도급인이 대금을 늦게 냄) | **미지급금액** |
   *
   * 한 이름으로 굳히면(예전 `paid_amount`) ① 을 부를 때 **안 낸 돈에 붙어야 할
   * 이자가 이미 낸 돈에 붙는다.**
   */
  base_amount: number;
  days: number;
  /** 연 이율(bp). 10000 = 연 100%. */
  rate_bp: number;
}

/**
 * 지연이자 = floor(원금 × 연이율 × 일수 / 365).
 *
 * **지체상금과 셈이 다르다** — 일당 천분율이 아니라 **연리를 일할**하고, 한도가
 * 원문에 없다. 원금이 무엇인지는 `base_amount` 주석의 표를 따른다.
 *
 * 365 는 **원문에 없는 가정**이다(원문은 「%의 연체이율」 이라고만 한다). 계약서
 * 관행이라 `DAYS_PER_YEAR` 로 드러내 둔다.
 */
export const lateInterestOf = (input: LateInterestInput): number => {
  checkedMoney(input.base_amount, "지연이자 원금");
  if (!Number.isInteger(input.days) || input.days < 0) {
    throw new Error("지체일수는 0 이상의 정수여야 합니다");
  }
  if (!Number.isInteger(input.rate_bp) || input.rate_bp < 0) {
    throw new Error("연체이율은 0 이상의 정수(bp)여야 합니다");
  }
  // 나누기 전에 본다 — 곱이 2^53 을 넘어도 몫은 안전정수라 검사를 빠져나간다.
  const scaled = checked(input.base_amount * input.rate_bp, "지연이자");
  return Math.floor(
    checked(scaled * input.days, "지연이자") / (BP_SCALE * DAYS_PER_YEAR)
  );
};

/* ── 지체상금 ─────────────────────────────────────────────────── */

/**
 * 하루치 지체상금 = floor(도급금액 × 요율). 계약서가 「1일당 도급금액의 1/1000」
 * 로 쓰이므로 하루치를 먼저 내고 일수를 곱한다.
 */
export const dailyDelayPenaltyOf = (amount: number, rateBp: number): number => {
  checkedMoney(amount, "도급금액");
  if (!Number.isInteger(rateBp) || rateBp < 0) {
    throw new Error("지체상금률은 0 이상의 정수(bp)여야 합니다");
  }
  return Math.floor(checked(amount * rateBp, "지체상금") / BP_SCALE);
};

export interface DelayPenaltyInput {
  amount: number;
  days: number;
  rate_bp: number;
  cap_bp: number;
}

/**
 * 지체상금 = min(하루치 × 지체일수, 도급금액 × 한도).
 *
 * **한도가 있는 이유** — 필로스 실물의 2/1000 은 공공공사 기준 1천분의 0.5(국가계약법 시행규칙 제75조)의 4배라
 * 민법 398조 감액 대상이다(노트 `12`). 한도 없이 쌓이면 90일 지체에 도급금액의
 * 18% 가 나오고, 그 조항은 재판에서 깎인다. 깎일 조항을 계약서에 넣지 않는다.
 */
export const delayPenaltyOf = (input: DelayPenaltyInput): number => {
  if (!Number.isInteger(input.days) || input.days < 0) {
    throw new Error("지체일수는 0 이상의 정수여야 합니다");
  }
  if (!Number.isInteger(input.cap_bp) || input.cap_bp < 0) {
    throw new Error("지체상금 한도는 0 이상의 정수(bp)여야 합니다");
  }
  const daily = dailyDelayPenaltyOf(input.amount, input.rate_bp);
  const cap = Math.floor(
    checked(input.amount * input.cap_bp, "지체상금 한도") / BP_SCALE
  );
  if (daily === 0) return 0;

  // **한도를 곱하기 전에 본다.** `daily × days` 를 먼저 재면 한도가 있는데도 큰
  // 일수에서 결과 대신 예외가 나온다(실측: 1천억원·1억일 → 답은 한도 100억인데
  // 「셀 수 있는 범위를 넘었다」). 한도를 넘길 일수면 곱하지 않는다.
  const maxDays = Math.floor(cap / daily);
  if (input.days > maxDays) return cap;
  return daily * input.days;
};

/* ── 위약금 ───────────────────────────────────────────────────── */

/**
 * 착수 전 해제 위약금 = floor(총 공사금액 × 위약금률).
 *
 * **상한이 조항 본문에 박혀 있다** — 공정위 제7조 ③ 1. 이 「총 공사금액의 10%를
 * 초과할 수 없음」 이다. 넘는 값을 계산해 주면 표준서식을 달고 무효인 계약서가
 * 나가므로 계산 전에 막는다. 제작·착수 이후(같은 조 2호)는 실손해 배상이라
 * 비율이 없고, 이 함수의 일이 아니다.
 */
export const penaltyOf = (
  total: number,
  rateBp: number,
  source: ClauseSource
): number => {
  checkedMoney(total, "계약금액");
  if (!Number.isInteger(rateBp) || rateBp < 0) {
    throw new Error("위약금률은 0 이상의 정수(bp)여야 합니다");
  }
  // **본문 상한은 공정위에만 있다.** 모든 서식에 걸었더니 국토부·자체 조항 계약도
  // 10% 에서 막혔다 — 없는 본문을 근거로 막는 것은 하자담보에서 이미 한 번 고친
  // 잘못이다(`assertWarrantyAgainstStandard`).
  //
  // **전역 상한보다 먼저 본다.** 둘 다 어긴 값(공정위 · 100.01%)에서 담당자에게
  // 필요한 말은 「공정위는 10%」 쪽이다 — 100% 를 말해 줘 봐야 고칠 수가 없다.
  if (source === "FTC_10079" && rateBp > PENALTY_RATE_CAP_BP) {
    throw new Error(
      "위약금은 총 공사금액의 10% 를 넘을 수 없습니다 (공정위 표준약관 제7조)"
    );
  }
  // **제정신 상한은 서식과 무관하다.** 서식별로만 막았더니 `CUSTOM` 계약에서
  // 위약금률 1000%(계약금액의 10배)가 통과했다(2026-09-22 리뷰).
  if (rateBp > BP_SCALE) {
    throw new Error("위약금률은 100% 를 넘을 수 없습니다");
  }
  return Math.floor(checked(total * rateBp, "위약금") / BP_SCALE);
};

/* ── 하자담보 ─────────────────────────────────────────────────── */

/**
 * 공종별 하자담보 예외가 성립하는가.
 *
 * **같은 공종이 두 번 나오면 막는다** — 「창호 24개월」 과 「창호 12개월」 이 같이
 * 있는 계약서는 어느 쪽이 맞는지 다투게 된다. 이름이 빈 줄도 같은 이유다.
 */
export const assertWarrantyTerms = (terms: readonly WarrantyTerm[]): void => {
  // **모양부터 본다.** jsonb 열이라 저장소·RPC 를 거쳐 온 값이 객체가 아닐 수 있고,
  // 그대로 `.trim()` 을 부르면 한국어 오류 대신 `TypeError` 가 화면까지 샌다
  // (「오류는 문구가 아니라 타입으로 가른다」 — 던지는 쪽도 지켜야 한다).
  if (!Array.isArray(terms)) {
    throw new Error("하자담보 예외는 목록이어야 합니다");
  }
  const seen = new Set<string>();
  for (const term of terms) {
    if (!term || typeof term !== "object" || typeof term.label !== "string") {
      throw new Error("하자담보 예외의 공종 이름이 비어 있습니다");
    }
    const label = term.label.trim();
    if (label === "") {
      throw new Error("하자담보 예외의 공종 이름이 비어 있습니다");
    }
    // 상한을 둔다 — 1,000,000개월짜리 하자담보는 입력 실수이지 약정이 아니다.
    if (!Number.isInteger(term.months) || term.months < 0 || term.months > 600) {
      throw new Error(
        `${label}의 하자담보 기간은 0 이상 600 이하의 정수(개월)여야 합니다`
      );
    }
    // **중복은 조회와 같은 자로 잰다.** `.trim()` 으로만 보던 시절 「창호 공사」와
    // 「창호공사」가 서로 다른 말로 통과했는데, 조회는 둘을 같게 봐서 **모순된 두
    // 줄이 한 계약에 남고 배열 순서가 승패를 정했다**(2026-09-22 4차 재리뷰).
    const key = warrantyLabelKey(label);
    if (seen.has(key)) {
      throw new Error(`하자담보 예외에 ${label}${subjectJosa(label)} 두 번 있습니다`);
    }
    seen.add(key);
  }
};

/**
 * 이 공종의 하자담보 기간(개월). 예외에 없으면 일반 기간이다.
 *
 * 예외가 **일반보다 짧아도 그대로 쓴다** — 공정위 제10조의 「유리는 1년」 이
 * 정확히 그 모양이다(창호 2년 안의 예외).
 */
export const warrantyMonthsOf = (
  label: string,
  generalMonths: number,
  terms: readonly WarrantyTerm[]
): number =>
  terms.find((t) => warrantyLabelKey(t.label) === warrantyLabelKey(label))?.months ??
  generalMonths;

/**
 * 하자담보 기간이 **표준 서식 본문보다 짧지 않은가**.
 *
 * 조항이 축자 원문이면 본문은 「창호 2년」 이라고 말한다. 그런데 열은 아무 값이나
 * 받으므로 `warranty_months = 6` 인 계약이 **그 본문과 「공정위 표준」 표지를 달고**
 * 나갈 수 있다 — 짧은 쪽은 약관법상 무효이고, 남는 것은 문서와 표지가 갈린 계약서다.
 *
 * **공정위 서식에만 건다.** 국토부 제31조에는 개월 수가 없고(계약서에 따로 적게 하고
 * 건산법 별표를 따른다), `CUSTOM` 은 회사가 직접 쓴 본문이라 우리가 뭘 약속했는지
 * 모른다. 그 둘에 공정위 수를 들이대면 **없는 본문을 인용하며 저장을 막는다.**
 *
 * 다만 **모양은 언제나 본다** — 기간 비교를 건너뛴다고 깨진 값까지 통과시키면,
 * 이 함수를 저장 직전 가드로 쓰는 쪽에서 앱 검사가 통째로 0 이 된다.
 */
export const assertWarrantyAgainstStandard = (
  source: ClauseSource,
  generalMonths: number,
  terms: readonly WarrantyTerm[]
): void => {
  assertWarrantyTerms(terms);
  if (source !== "FTC_10079") return;

  if (generalMonths < FTC_MIN_WARRANTY_MONTHS.general) {
    throw new Error(
      `하자담보 기간이 표준계약서 본문(${FTC_MIN_WARRANTY_MONTHS.general}개월)보다 짧습니다`
    );
  }
  // **예외 목록에서만 찾는다.** 「일반」 같은 센티널 이름을 두면 그 이름으로 저장된
  // 예외가 검사를 비켜 간다(실측: label 「일반」 1개월이 그대로 통과했다).
  for (const [label, min] of Object.entries(FTC_MIN_WARRANTY_MONTHS.byLabel)) {
    // **예외가 없는 것 자체는 결함이 아니다.** 일반 기간이 이미 본문을 넘으면
    // 모순이 없다 — 「유리 1년」 은 일반 12개월과 같은 수라 그 행이 없어도 되고,
    // 일반을 24개월로 넉넉히 준 계약도 마찬가지다. 예외 유무로 가르던 판은
    // **그 두 계약을 저장 불가로 만들었다**(2026-09-22 4차 재리뷰).
    if (warrantyMonthsOf(label, generalMonths, terms) >= min) continue;

    // 여기까지 오면 실제로 짧다. **원인만 나눠 말한다** — 이름을 「창호」로 적고
    // 24개월을 넣은 회사가 「24를 적었는데 24보다 짧다」 는 말을 들으면 화면에서
    // 풀 방법이 없다.
    const found = terms.some(
      (t) => warrantyLabelKey(t.label) === warrantyLabelKey(label)
    );
    throw new Error(
      found
        ? `${label}의 하자담보 기간이 표준계약서 본문(${min}개월)보다 짧습니다`
        : `표준계약서 본문이 정한 「${label} ${min}개월」 이 하자담보 예외에 없습니다` +
          ` — 공종 이름을 「${label}」 로 적어 주세요`
    );
  }
};

/**
 * 특약이 성립하는가. **DB 는 문자열인지까지만 본다**(`contract_special_terms_shape`)
 * — 빈 제목·빈 본문은 통과하므로 여기서 막는다. 제목도 내용도 없는 특약은
 * 계약서에 「제1조 」 한 줄로 인쇄된다.
 */
export const assertSpecialTerms = (terms: readonly SpecialTerm[]): void => {
  if (!Array.isArray(terms)) {
    throw new Error("특약은 목록이어야 합니다");
  }
  const seen = new Set<number>();
  terms.forEach((term, i) => {
    if (
      !term ||
      typeof term !== "object" ||
      typeof term.title !== "string" ||
      typeof term.body !== "string"
    ) {
      throw new Error(`${i + 1}번째 특약의 모양이 올바르지 않습니다`);
    }
    if (term.title.trim() === "") {
      throw new Error(`${i + 1}번째 특약의 제목이 비어 있습니다`);
    }
    if (term.body.trim() === "") {
      throw new Error(`${i + 1}번째 특약(${term.title.trim()})의 내용이 비어 있습니다`);
    }
    // `no` 는 **인쇄 번호**다. 문자열·음수·중복이 들어오면 계약서에 「제-3조」·
    // 「제1조」 두 개가 찍힌다. DB check 는 `title`·`body` 만 보므로 여기서 막는다.
    if (!Number.isInteger(term.no) || term.no < 1) {
      throw new Error(`${i + 1}번째 특약의 번호가 올바르지 않습니다`);
    }
    if (seen.has(term.no)) {
      throw new Error(`특약 번호 ${term.no}${subjectJosa(String(term.no))} 두 번 있습니다`);
    }
    seen.add(term.no);
  });
};
